"use client";

import React from "react";
import { ShieldCheck, Award, Activity, TrendingUp, CircleCheck, ArrowRight } from "lucide-react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  stage: "Verify",
  optima: 92,
  standard: 75
}, {
  stage: "Code",
  optima: 98,
  standard: 82
}, {
  stage: "Submit",
  optima: 99,
  standard: 88
}, {
  stage: "Audit",
  optima: 97,
  standard: 80
}, {
  stage: "Paid",
  optima: 99,
  standard: 84
}];
const chartConfig = {
  optima: {
    label: "Ferio Solutions",
    color: "var(--chart-2)"
  },
  standard: {
    label: "Industry Avg",
    color: "var(--chart-5)"
  }
} satisfies ChartConfig;
const pillars = [{
  title: "Regulatory Knowledge",
  description: "Deep expertise in HIPAA compliance and payer-specific requirements to ensure zero-penalty audits and data security.",
  icon: ShieldCheck
}, {
  title: "Reduced Denials",
  description: "Precision-driven coding and claim scrubbing protocols that minimize rejections and maximize first-pass payment rates.",
  icon: Award
}, {
  title: "Patient Experience",
  description: "Dedicated financial support services providing clear billing guidance and resolution for patient inquiries.",
  icon: Activity
}, {
  title: "Revenue Velocity",
  description: "Real-time RCM analytics and reporting tools that offer actionable insights to optimize your practice's cash flow.",
  icon: TrendingUp
}];
const certifications = ["HIPAA Compliant", "AAPC Certified", "HBMA Member"];
export default function ClinicalExcellence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="38937" id="why-providers-trust-us" className="bg-background text-foreground py-24 relative overflow-hidden" ref={ref}>
      <div className="container mx-auto px-6 md:px-12">
        <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          
          {}
          <div className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 tracking-tighter font-default">
              The Velocity of Trust & <span className="text-chart-2">Financial Precision</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
              Ferio Solutions integrates advanced medical billing expertise with a pulse on regulatory shifts. 
              We move beyond traditional administrative support to establish a high-performance RCM system 
              defined by accurate coding, rigorous compliance, and accelerated payment cycles.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {}
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
              {pillars.map((pillar, i) => <Card key={i} data-index={i} className="bg-card border-border/50 shadow-sm hover:shadow-lg transition-all duration-300 rounded-none border-l-4 border-l-primary/10 hover:border-l-chart-2">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-muted rounded-none flex items-center justify-center mb-4 text-chart-2">
                      <pillar.icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-xl font-bold text-primary font-default">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed text-muted-foreground font-sans">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </Card>)}
            </div>

            {}
            <div className="lg:col-span-5 flex flex-col gap-8">
              
              {}
              <Card className="rounded-none border-border shadow-md bg-card overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg font-bold flex items-center gap-2 font-default">
                    <Activity className="w-5 h-5 text-chart-2" />
                    Revenue Resolution Speed
                  </CardTitle>
                  <CardDescription className="font-sans">
                    Comparative claim success rates through the lifecycle.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-6 pl-0 pr-6">
                  <div className="h-[250px] w-full">
                    <ChartContainer config={chartConfig} className="h-full w-full">
                      <LineChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }}>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="stage" tickLine={false} axisLine={false} tickMargin={10} tick={{
                        fill: "var(--muted-foreground)",
                        fontSize: 12,
                        fontFamily: "var(--font-mono)"
                      }} />
                        <YAxis hide domain={[0, 100]} />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                        <Line dataKey="optima" type="monotone" stroke="var(--color-chart-2)" strokeWidth={3} dot={{
                        r: 4,
                        fill: "var(--color-chart-2)"
                      }} activeDot={{
                        r: 6
                      }} />
                        <Line dataKey="standard" type="monotone" stroke="var(--color-chart-5)" strokeWidth={2} strokeDasharray="5 5" dot={{
                        r: 3,
                        fill: "var(--color-chart-5)"
                      }} activeDot={{
                        r: 5
                      }} />
                      </LineChart>
                    </ChartContainer>
                  </div>
                </CardContent>
              </Card>

              {}
              <div className="bg-primary/5 p-8 border border-primary/10 rounded-none backdrop-blur-sm">
                <h3 className="text-xs font-bold uppercase tracking-widest text-primary mb-4 font-default">
                  Institutional Certifications
                </h3>
                <div className="flex flex-wrap gap-3 mb-8">
                  {certifications.map((cert, i) => <Badge key={i} data-index={i} variant="secondary" className="bg-card text-primary border-border px-3 py-1.5 text-xs font-medium rounded-none shadow-xs font-mono">
                      <CircleCheck className="w-3.5 h-3.5 mr-1.5 text-chart-3" />
                      {cert}
                    </Badge>)}
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4 border-t border-primary/10">
                  <Button asChild variant="ghost" className="text-primary hover:text-chart-2 hover:bg-transparent pl-0 hover:pl-2 transition-all duration-300 group text-base font-bold font-default">
                    <Link to="#">
                      Request Practice Audit
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
}